import { Map as ImmutableMap, is, Record, type RecordOf } from 'immutable';
import { createSelector as createSelectorBase } from 'reselect';

import type RootState from 'app-components/utils/redux-store/rootState';

import { type ACCOUNTS_REDUCER_KEY, accountsSelectors } from '../accounts';
import type { Account } from '../accounts/accountsTypes';
import type { DATE_REDUCER_KEY } from '../date/dateTypes';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';
import { type PREFERENCES_V2_REDUCER_KEY, preferencesV2Selectors } from '../preferences-v2';
import type { SECURITIES_REDUCER_KEY } from '../securities/securitiesTypes';
import { type AccountBalances, AccountBalancesRecord, ShowBalance } from './balanceTypes';
import { type AccountBalanceInput, getBalanceForAccount } from './balanceUtils';

export { getBalanceForAccount } from './balanceUtils';

type State = Pick<
  RootState,
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof SECURITIES_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
  | typeof PREFERENCES_V2_REDUCER_KEY
>;
const createSelector = createSelectorBase.withTypes<State>();

function balRound(val: number): number {
  const ret = val ? Number(val.toFixed(2)) : 0;
  return ret || 0;
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
//                                Standard Selectors
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// expects props: (state, accountId)
export const getBalanceForAccountId = createSelector(
  [
    (state, accountId?: string) => accountsSelectors.getAccountById(state, accountId),
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'sAccountsBankBalance', ShowBalance.INCLUDE_PENDING),
  ],
  getBalanceForAccount,
);

type AggregateBalanceAccount = AccountBalanceInput & Pick<Account, 'id' | 'goalBalance'>;
const aggregateBalanceAccount = Record<AggregateBalanceAccount>({
  id: undefined,
  type: 'UNKNOWN',
  isClosed: undefined,
  isConnected: undefined,
  institutionLoginId: undefined,
  currentBalanceAsOf: undefined,
  onlineBalance: undefined,
  goalBalance: undefined,
});

// Only fields used by the balance calculation should invalidate every account's displayed balance.
const getBalanceAccounts = createSelector(
  [accountsSelectors.getAccountsById],
  (accounts): ImmutableMap<string, RecordOf<AggregateBalanceAccount>> =>
    accounts.map((account) =>
      aggregateBalanceAccount({
        id: account.id,
        type: account.type,
        isClosed: account.isClosed,
        isConnected: account.isConnected,
        institutionLoginId: account.institutionLoginId,
        currentBalanceAsOf: account.currentBalanceAsOf,
        onlineBalance: account.onlineBalance,
        goalBalance: account.goalBalance,
      }),
    ),
  { memoizeOptions: { resultEqualityCheck: is } },
);

/** Select every displayed balance from the supplied snapshot. Use getBalanceForAccountId for a single account. */
export const getBalancesByAccountIds = createSelector(
  [
    getBalanceAccounts,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'investmentAccountGoalContributions'),
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'sAccountsBankBalance', ShowBalance.INCLUDE_PENDING),
  ],
  (accountsById, investmentAccountGoalContributions, balanceType): ImmutableMap<string, RecordOf<AccountBalances>> =>
    ImmutableMap<string, RecordOf<AccountBalances>>().withMutations((balances) => {
      accountsById.forEach((account) => {
        if (!account.id) return;
        const balance = getBalanceForAccount(account, balanceType);
        const goalsBalance =
          account.type !== 'INVESTMENT' || investmentAccountGoalContributions ? account.goalBalance || 0 : 0;
        balances.set(
          account.id,
          AccountBalancesRecord({
            accountId: account.id,
            accountType: account.type,
            balance,
            goalsBalance,
            availableBalance: balRound(balance - goalsBalance),
          }),
        );
      });
    }),
  { memoizeOptions: { resultEqualityCheck: is } },
);
